import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Typography, Button, Alert, Tag, Empty } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '../services/api';

const { Title, Text } = Typography;

interface Dispute {
  id: string;
  amount: number;      // già in euro, non in centesimi
  currency: string;
  reason: string;
  status: string;
  created: string;
}

const STATO: Record<string, { t: string; c: string }> = {
  needs_response:         { t: 'Da rispondere', c: 'warning' },
  warning_needs_response: { t: 'Da rispondere', c: 'warning' },
  under_review:           { t: 'In esame',      c: 'processing' },
  won:                    { t: 'Vinta',         c: 'success' },
  lost:                   { t: 'Persa',         c: 'error' },
};

// I codici di Stripe sono in inglese: qui diventano leggibili.
const MOTIVO: Record<string, string> = {
  duplicate:             'Addebito doppio',
  fraudulent:            'Non riconosciuto dal titolare',
  subscription_canceled: 'Abbonamento già disdetto',
  product_not_received:  'Servizio non ricevuto',
  product_unacceptable:  'Servizio non conforme',
  credit_not_processed:  'Rimborso non ricevuto',
  general:               'Generico',
};

export default function DisputesPage() {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const res = await adminApi.disputes();
      setDisputes(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Non è stato possibile caricare le contestazioni.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const colonne: ColumnsType<Dispute> = [
    {
      title: 'Data',
      dataIndex: 'created',
      width: 130,
      sorter: (a, b) => +new Date(a.created) - +new Date(b.created),
      defaultSortOrder: 'descend',
      render: d => {
        const t = new Date(d);
        return isNaN(+t) ? d : t.toLocaleDateString('it-IT');
      },
    },
    {
      title: 'Importo',
      dataIndex: 'amount',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.amount - b.amount,
      render: v => (
        <Text strong style={{ color: '#DC3055' }}>
          € {Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      render: r => MOTIVO[r] || (r ? String(r).replace(/_/g, ' ') : '—'),
    },
    {
      title: 'Stato',
      dataIndex: 'status',
      width: 155,
      render: s => {
        const x = STATO[s];
        return x
          ? <Tag color={x.c}>{x.t}</Tag>
          : <Tag>{s ? String(s).replace(/_/g, ' ') : '—'}</Tag>;
      },
    },
    {
      title: 'Riferimento',
      dataIndex: 'id',
      width: 200,
      responsive: ['lg'],
      render: v => <Text type="secondary" copyable style={{ fontSize: 12 }}>{v}</Text>,
    },
  ];

  return (
    <>
      <div className="page-top">
        <div>
          <Title level={3} style={{ margin: 0 }}>Dispute</Title>
          <Text type="secondary">
            Contestazioni aperte dai clienti presso la loro banca
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
      </div>

      {errore && (
        <Alert
          type="error" showIcon style={{ marginBottom: 16 }}
          message="Caricamento non riuscito"
          description={<span data-testid="disputes-error">{errore}</span>}
          action={<Button size="small" onClick={load}>Riprova</Button>}
        />
      )}

      <Card styles={{ body: { padding: 0 } }}>
        <Table<Dispute>
          rowKey="id"
          columns={colonne}
          dataSource={disputes}
          loading={loading}
          pagination={{ pageSize: 20, hideOnSinglePage: true, style: { padding: '0 20px' } }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nessuna contestazione. È la situazione che vuoi."
              />
            ),
          }}
          scroll={{ x: 700 }}
        />
      </Card>
    </>
  );
}
